'use client';

import React, {
  ComponentPropsWithRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { EmblaCarouselType } from 'embla-carousel';

import { Box, SystemIcon } from '@wishket/design-system';

import { useCarouselContext } from './CarouselProvider';

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};

const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined
): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

type ButtonProps = ComponentPropsWithRef<'button'>;

const buttonStyle =
  'border-1 flex size-8 items-center justify-center gap-2 rounded-full border border-w-gray-100 p-2';
export const PrevButton = ({ disabled, ...props }: ButtonProps) => {
  return (
    <button className={buttonStyle} disabled={disabled} {...props}>
      <SystemIcon
        name="medium_arrow_left"
        className={`${disabled && 'text-w-gray-300'}`}
      />
    </button>
  );
};

const NextButton = ({ disabled, ...props }: ButtonProps) => {
  return (
    <button className={buttonStyle} disabled={disabled} {...props}>
      <SystemIcon
        name="medium_arrow_right"
        className={`${disabled && 'text-w-gray-300'}`}
      />
    </button>
  );
};

export const CardCarouselPrevNextButtons = () => {
  const { emblaApi } = useCarouselContext();
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  return (
    <Box className="hidden gap-3 desktop:flex">
      <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
      <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
    </Box>
  );
};
